import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import themes, { styled } from '~/themes';
import { Formik, Form } from 'formik';
import {
  Box,
  Divider,
  Grid,
  MenuItem,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import Button from '~/components/common/Button';
import { Field } from '~/components/common/Formik';
import {
  ONGOING_STATUS_OPTIONS,
  COMPLETED_STATUS_OPTIONS,
  RATING_OPTIONS,
} from '~/constants/task';
import { fromQueryString, toQueryString } from '~/utils/queryString';
import { ITabValue } from '~/models/shared';
import { debounce, get, isEqual } from 'lodash';
import { Icon } from '~/components/common/Icon';
import { IStore } from '~/models/stores';
import { usePaymentInfo } from '~/providers/PaymentProvider';
import { TimeRangePicker } from '~/components/common/TimeRangePicker';
import { DateRangePicker } from '~/components/common/DateRangePicker';
import moment from 'moment';
import { UserStatus } from '~/models/user';
import { useGetDriverListQuery } from '../AdminOptions/Drivers/apis';
import { UserRole } from '../AdminOptions/Users/types';
import { fetchSuggestTaskList } from '../LiveTracking/apis';

export const StyledForm = styled(Form)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

export const StyledFilterStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  rowGap: theme.spacing(2),
  columnGap: theme.spacing(0.8),
}));

interface IFilterFormProps {
  onSubmit?: (values: any) => void;
  onClear?: () => void;
  onClearAll?: () => void;
  listStore?: IStore[];
}

const defaultValues = {
  search: '',
  executor_ids: '',
  statuses: '',
  created_by: '',
  name: '',
  pickup_location: '',
  dropoff_location: '',
  recipient: '',
  rating: '',
  schedule_time_from: '',
  schedule_time_to: '',
  schedule_date_from: '',
  schedule_date_to: '',
  pickedup_time_from: '',
  pickedup_time_to: '',
  pickedup_date_from: '',
  pickedup_date_to: '',
  complete_time_from: '',
  complete_time_to: '',
  complete_date_from: '',
  complete_date_to: '',
};

export const FilterForm: React.FC<IFilterFormProps> = ({
  onSubmit,
  onClear,
  onClearAll,
  listStore = [],
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef<any>(null);
  const { isBundlePlan } = usePaymentInfo();
  const isMounted = useRef(false);

  const {
    tab,
    search,
    statuses,
    executor_ids,
    created_by,
    name,
    pickup_location,
    dropoff_location,
    recipient,
    rating,
    schedule_time_from,
    schedule_time_to,
    schedule_date_from,
    schedule_date_to,
    pickedup_time_from,
    pickedup_time_to,
    pickedup_date_from,
    pickedup_date_to,
    complete_time_from,
    complete_time_to,
    complete_date_from,
    complete_date_to,
  } = fromQueryString(location.search, { arrayFormat: 'none' });
  const tabValue = (tab || ITabValue.Ongoing) as ITabValue;
  const [isExpand, setIsExpand] = useState(() => {
    if (
      name ||
      pickup_location ||
      dropoff_location ||
      recipient ||
      rating ||
      schedule_time_from ||
      schedule_time_to ||
      schedule_date_from ||
      complete_date_from ||
      pickedup_date_from ||
      pickedup_time_from ||
      pickedup_time_to ||
      complete_time_to ||
      complete_time_from
    )
      return true;
    return false;
  });

  const initialValues = {
    search: search || '',
    executor_ids: executor_ids || '',
    statuses: statuses || '',
    created_by: created_by || '',
    name: name || '',
    pickup_location: pickup_location || '',
    dropoff_location: dropoff_location || '',
    recipient: recipient || '',
    rating: rating || '',
    schedule_time_from: schedule_time_from || '',
    schedule_time_to: schedule_time_to || '',
    schedule_date_from: schedule_date_from || '',
    schedule_date_to: schedule_date_to || '',
    pickedup_time_from: pickedup_time_from || '',
    pickedup_time_to: pickedup_time_to || '',
    pickedup_date_from: pickedup_date_from || '',
    pickedup_date_to: pickedup_date_to || '',
    complete_time_from: complete_time_from || '',
    complete_time_to: complete_time_to || '',
    complete_date_from: complete_date_from || '',
    complete_date_to: complete_date_to || '',
  };

  const { data: listDrivers } = useGetDriverListQuery({
    roles: UserRole.Driver,
    status: [UserStatus.Active, UserStatus.Deleted],
  });

  const memoizedDrivers = useMemo(() => {
    if (!listDrivers || listDrivers.length === 0) {
      return {
        deletedDrivers: [],
        activeDrivers: [],
      };
    }

    return {
      deletedDrivers: listDrivers.filter(
        (driver) => driver.status === 'deleted',
      ),
      activeDrivers: listDrivers.filter(
        (driver) => driver.status !== 'deleted',
      ),
    };
  }, [listDrivers]);

  const handleSearch = useCallback(
    debounce(() => {
      formRef.current?.handleSubmit();
    }, 600),
    [],
  );

  useLayoutEffect(() => {
    if (isMounted.current) {
      setIsExpand(false);
    } else {
      isMounted.current = false;
    }
  }, [tabValue]);

  const renderDriverList = useCallback(
    ({ setFieldValue }) => {
      const options = [];

      // Add active drivers first
      if (memoizedDrivers.activeDrivers.length > 0) {
        options.push(
          ...memoizedDrivers.activeDrivers.map((driver) => ({
            label: driver.display_name,
            value: driver.id,
            key: driver.id,
          })),
        );
      }

      // Add separator and deleted drivers if any exist
      if (memoizedDrivers.deletedDrivers.length > 0) {
        // Add separator
        options.push({
          label: 'Deleted drivers',
          value: '',
          key: 'deleted-separator',
          disabled: true,
        });

        // Add deleted drivers
        options.push(
          ...memoizedDrivers.deletedDrivers.map((driver) => ({
            label: driver.display_name,
            value: driver.id,
            key: driver.id,
          })),
        );
      }

      return (
        <Field.Select
          key='driver'
          placeholder='Driver'
          name='executor_ids'
          {...(isExpand && { legend: 'Driver', placeholder: 'Select option' })}
          options={options}
          sortOption={false}
          sxPlaceholder={{
            color: `${themes.color.grayPlaceHolder} !important`,
          }}
          renderOption={(option) => (
            <MenuItem
              sx={{
                ...(option.key === 'deleted-separator' && {
                  'paddingLeft': '6px !important',
                  'backgroundColor': 'white !important',
                  'color': '#33363D !important',
                  'fontWeight': '500',
                  'fontSize': '12px',
                  'cursor': 'default !important',
                  '&:hover': {
                    backgroundColor: 'white !important',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'white !important',
                  },
                }),
              }}
              key={option?.key}
              value={option?.value || ''}
              disabled={option?.disabled}
            >
              {option?.label}
            </MenuItem>
          )}
          onChange={() => {
            if (!isExpand) formRef.current.handleSubmit();
          }}
          onClear={() => {
            setFieldValue('executor_ids', '');
            if (!isExpand) formRef.current.handleSubmit();
          }}
        />
      );
    },
    [memoizedDrivers, isExpand],
  );

  const renderTaskStatus = useCallback(
    ({ setFieldValue }) => (
      <Field.Select
        placeholder='Status'
        name='statuses'
        {...(isExpand && { legend: 'Status', placeholder: 'Select option' })}
        options={
          tabValue === ITabValue.Ongoing
            ? ONGOING_STATUS_OPTIONS
            : COMPLETED_STATUS_OPTIONS
        }
        sxPlaceholder={{
          color: `${themes.color.grayPlaceHolder} !important`,
        }}
        onChange={() => {
          if (!isExpand) formRef.current.handleSubmit();
        }}
        onClear={() => {
          setFieldValue('statuses', '');
          if (!isExpand) formRef.current.handleSubmit();
        }}
      />
    ),
    [tabValue, isExpand],
  );

  const renderRating = useCallback(
    ({ setFieldValue }) => (
      <Field.Select
        placeholder='Rating'
        name='rating'
        {...(isExpand && { legend: 'Rating', placeholder: 'Select option' })}
        options={RATING_OPTIONS}
        renderOption={(option) => (
          <MenuItem
            sx={{
              'display': 'flex',
              'alignItems': 'center',
              '&.Mui-selected': {
                backgroundColor: '#EDF0F7 !important',
              },
            }}
            key={option?.value}
            value={option?.value || ''}
            onClick={() => setFieldValue('rating', option.value)}
          >
            <Rating value={option.value} readOnly size='small' />
          </MenuItem>
        )}
        renderValue={(val) =>
          val ? (
            <Rating size='small' value={val} readOnly />
          ) : (
            <Typography
              sx={{
                lineHeight: 1,
                color: `${themes.color.grayPlaceHolder} !important`,
              }}
            >
              {isExpand ? 'Select option' : 'Rating'}
            </Typography>
          )
        }
        onChange={() => {
          if (!isExpand) formRef.current.handleSubmit();
        }}
        onClear={() => {
          setFieldValue('rating', '');
          if (!isExpand) formRef.current.handleSubmit();
        }}
      />
    ),
    [tabValue, isExpand],
  );

  const renderStore = useCallback(
    ({ setFieldValue }) => (
      <Field.Select
        key='created_by'
        placeholder='Store'
        name='created_by'
        {...(isExpand && { legend: 'Store', placeholder: 'Select option' })}
        options={
          listStore && listStore.length > 0
            ? [
                ...listStore.map((store) => ({
                  label: store.display_name,
                  value: store.id,
                  key: store.id,
                })),
              ]
            : []
        }
        sortOption
        sxPlaceholder={{
          color: `${themes.color.grayPlaceHolder} !important`,
        }}
        onChange={() => {
          if (!isExpand) formRef.current.handleSubmit();
        }}
        onClear={() => {
          setFieldValue('created_by', '');
          if (!isExpand) formRef.current.handleSubmit();
        }}
      />
    ),
    [listStore, isExpand],
  );

  const AsyncSelectField = ({
    fieldName,
    fieldKey,
    legend,
    placeholder,
    setFieldValue,
  }) => (
    <Field.AsyncSelect
      legend={legend}
      name={fieldName}
      placeholder={placeholder}
      uniqueValue
      onBlur={(value) => {
        setTimeout(() => {
          setFieldValue(fieldName, value || '');
        }, 50);
      }}
      onClear={() => setFieldValue(fieldName, '')}
      fetchOptions={(debouncedSearchText) =>
        fetchSuggestTaskList({
          field: fieldName,
          search: debouncedSearchText,
          type: tabValue === ITabValue.Ongoing ? 'ongoing' : 'completed',
        })
      }
      defaultOptions={[]}
      transformOptions={(data: any) => {
        if (!data?.length) return [];
        return data.map((item: any) => ({
          customLabel: get(item, fieldKey) || '',
          value: get(item, fieldKey),
          inputValue: get(item, fieldKey),
          label: '',
        }));
      }}
      onChange={(_, option) => {
        setFieldValue(fieldName, option?.customLabel);
      }}
    />
  );

  const renderForm = ({ values, setFieldValue, resetForm, dirty }) => {
    if (isExpand)
      return (
        <StyledForm
          sx={{
            '& .MuiOutlinedInput-input': {
              '&::placeholder': {
                color: 'rgba(51, 54, 61, 0.40) !important',
              },
            },
            '& .MuiInputAdornment-root': {
              marginRight: '0px !important',
              marginLeft: '0px !important',
            },
            '& input': {
              paddingRight: '0 !important',
            },
          }}
        >
          <Grid
            container
            spacing={1.6}
            sx={{
              '.MuiAutocomplete-input': {
                paddingRight: '15px !important',
              },
            }}
          >
            <Grid
              item
              sx={{
                flexBasis: {
                  xs: '50%',
                  sm: '33.33%',
                  xl: isBundlePlan ? '25%' : '20%',
                },
                maxWidth: {
                  xs: '50%',
                  sm: '33.33%',
                  xl: isBundlePlan ? '25%' : '20%',
                },
                flexGrow: 0,
              }}
            >
              <AsyncSelectField
                fieldKey='name'
                fieldName='name'
                legend='Task/order'
                placeholder='Search'
                setFieldValue={setFieldValue}
              />
            </Grid>
            <Grid
              item
              sx={{
                flexBasis: {
                  xs: '50%',
                  sm: '33.33%',
                  xl: isBundlePlan ? '25%' : '20%',
                },
                maxWidth: {
                  xs: '50%',
                  sm: '33.33%',
                  xl: isBundlePlan ? '25%' : '20%',
                },
                flexGrow: 0,
              }}
            >
              {renderTaskStatus({
                setFieldValue,
              })}
            </Grid>
            {!isBundlePlan && (
              <Grid
                item
                sx={{
                  flexBasis: {
                    xs: '50%',
                    sm: '33.33%',
                    xl: isBundlePlan ? '25%' : '20%',
                  },
                  maxWidth: {
                    xs: '50%',
                    sm: '33.33%',
                    xl: isBundlePlan ? '25%' : '20%',
                  },
                  flexGrow: 0,
                }}
              >
                {renderStore({
                  setFieldValue,
                })}
              </Grid>
            )}
            {tabValue === ITabValue.Ongoing && (
              <Grid
                item
                sx={{
                  flexBasis: {
                    xs: '100%',
                    sm: '33.33%',
                    xl: isBundlePlan ? '25%' : '20%',
                  },
                  maxWidth: {
                    xs: '100%',
                    sm: '33.33%',
                    xl: isBundlePlan ? '25%' : '20%',
                  },
                  flexGrow: 0,
                }}
              >
                <Grid container spacing={0.5}>
                  <Grid item xs={6}>
                    <TimeRangePicker
                      legend='Scheduled time'
                      value={{
                        from:
                          values?.schedule_time_from &&
                          moment(values?.schedule_time_from, 'HH:mm').format(
                            'hh:mm a',
                          ),
                        to:
                          values?.schedule_time_to &&
                          moment(values?.schedule_time_to, 'HH:mm').format(
                            'hh:mm a',
                          ),
                      }}
                      onChange={({ from, to }) => {
                        setFieldValue(
                          'schedule_time_from',
                          from ? moment(from, 'hh:mm a').format('HH:mm') : '',
                        );
                        setFieldValue(
                          'schedule_time_to',
                          to ? moment(to, 'hh:mm a').format('HH:mm') : '',
                        );
                      }}
                      onClear={() => {
                        setFieldValue('schedule_time_from', '');
                        setFieldValue('schedule_time_to', '');
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DateRangePicker
                      legend='Scheduled time'
                      hideLegend
                      value={{
                        from: values?.schedule_date_from as string,
                        to: values?.schedule_date_to as string,
                      }}
                      onChange={({ from, to }) => {
                        setFieldValue('schedule_date_from', from);
                        setFieldValue('schedule_date_to', to);
                      }}
                      onClear={() => {
                        setFieldValue('schedule_date_from', '');
                        setFieldValue('schedule_date_to', '');
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid
              item
              sx={{
                flexBasis: {
                  xs: '50%',
                  sm: '33.33%',
                  xl: isBundlePlan ? '25%' : '20%',
                },
                maxWidth: {
                  xs: '50%',
                  sm: '33.33%',
                  xl: isBundlePlan ? '25%' : '20%',
                },
                flexGrow: 0,
              }}
            >
              {renderDriverList({
                setFieldValue,
              })}
            </Grid>
            <Grid
              item
              sx={{
                flexBasis: {
                  xs: '50%',
                  sm: '33.33%',
                  xl: isBundlePlan ? '25%' : '20%',
                },
                maxWidth: {
                  xs: '50%',
                  sm: '33.33%',
                  xl: isBundlePlan ? '25%' : '20%',
                },
                flexGrow: 0,
              }}
            >
              <AsyncSelectField
                fieldKey='pickup_location'
                fieldName='pickup_location'
                legend='Pickup location'
                placeholder='Search'
                setFieldValue={setFieldValue}
              />
            </Grid>
            {tabValue === ITabValue.Ongoing && (
              <Grid
                item
                sx={{
                  flexBasis: {
                    xs: '100%',
                    sm: '33.33%',
                    xl: isBundlePlan ? '25%' : '20%',
                  },
                  maxWidth: {
                    xs: '100%',
                    sm: '33.33%',
                    xl: isBundlePlan ? '25%' : '20%',
                  },
                  flexGrow: 0,
                }}
              >
                <Grid container spacing={0.5}>
                  <Grid item xs={6}>
                    <TimeRangePicker
                      legend='Picked up time'
                      value={{
                        from:
                          values?.pickedup_time_from &&
                          moment(values?.pickedup_time_from, 'HH:mm').format(
                            'hh:mm a',
                          ),
                        to:
                          values?.pickedup_time_to &&
                          moment(values?.pickedup_time_to, 'HH:mm').format(
                            'hh:mm a',
                          ),
                      }}
                      onChange={({ from, to }) => {
                        setFieldValue(
                          'pickedup_time_from',
                          from ? moment(from, 'hh:mm a').format('HH:mm') : '',
                        );
                        setFieldValue(
                          'pickedup_time_to',
                          to ? moment(to, 'hh:mm a').format('HH:mm') : '',
                        );
                      }}
                      onClear={() => {
                        setFieldValue('pickedup_time_from', '');
                        setFieldValue('pickedup_time_to', '');
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DateRangePicker
                      legend='Pickedup time'
                      hideLegend
                      value={{
                        from: values?.pickedup_date_from as string,
                        to: values?.pickedup_date_to as string,
                      }}
                      onChange={({ from, to }) => {
                        setFieldValue('pickedup_date_from', from);
                        setFieldValue('pickedup_date_to', to);
                      }}
                      onClear={() => {
                        setFieldValue('pickedup_date_from', '');
                        setFieldValue('pickedup_date_to', '');
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid
              item
              sx={{
                flexBasis: {
                  xs: '50%',
                  sm: '33.33%',
                  xl: isBundlePlan ? '25%' : '20%',
                },
                maxWidth: {
                  xs: '50%',
                  sm: '33.33%',
                  xl: isBundlePlan ? '25%' : '20%',
                },
                flexGrow: 0,
              }}
            >
              <AsyncSelectField
                fieldKey='delivery.name'
                fieldName='recipient'
                legend='Recipient'
                placeholder='Search'
                setFieldValue={setFieldValue}
              />
            </Grid>
            <Grid
              item
              sx={{
                flexBasis: {
                  xs: '50%',
                  sm: '33.33%',
                  xl: isBundlePlan ? '25%' : '20%',
                },
                maxWidth: {
                  xs: '50%',
                  sm: '33.33%',
                  xl: isBundlePlan ? '25%' : '20%',
                },
                flexGrow: 0,
              }}
            >
              <AsyncSelectField
                fieldKey='dropoff_location'
                fieldName='dropoff_location'
                legend='Dropoff location'
                placeholder='Search'
                setFieldValue={setFieldValue}
              />
            </Grid>
            {tabValue === ITabValue.Completed && (
              <Grid
                item
                sx={{
                  flexBasis: {
                    xs: '100%',
                    sm: '33.33%',
                    xl: isBundlePlan ? '25%' : '20%',
                  },
                  maxWidth: {
                    xs: '100%',
                    sm: '33.33%',
                    xl: isBundlePlan ? '25%' : '20%',
                  },
                  flexGrow: 0,
                }}
              >
                <Grid container spacing={0.5}>
                  <Grid item xs={6}>
                    <TimeRangePicker
                      legend='Completed time'
                      value={{
                        from:
                          values?.complete_time_from &&
                          moment(values?.complete_time_from, 'HH:mm').format(
                            'hh:mm a',
                          ),
                        to:
                          values?.complete_time_to &&
                          moment(values?.complete_time_to, 'HH:mm').format(
                            'hh:mm a',
                          ),
                      }}
                      onChange={({ from, to }) => {
                        setFieldValue(
                          'complete_time_from',
                          from ? moment(from, 'hh:mm a').format('HH:mm') : '',
                        );
                        setFieldValue(
                          'complete_time_to',
                          to ? moment(to, 'hh:mm a').format('HH:mm') : '',
                        );
                      }}
                      onClear={() => {
                        setFieldValue('complete_time_from', '');
                        setFieldValue('complete_time_to', '');
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DateRangePicker
                      legend='Complete time'
                      hideLegend
                      value={{
                        from: values?.complete_date_from as string,
                        to: values?.complete_date_to as string,
                      }}
                      onChange={({ from, to }) => {
                        setFieldValue('complete_date_from', from);
                        setFieldValue('complete_date_to', to);
                      }}
                      onClear={() => {
                        setFieldValue('complete_date_from', '');
                        setFieldValue('complete_date_to', '');
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            {tabValue === ITabValue.Completed && (
              <Grid
                item
                sx={{
                  flexBasis: {
                    xs: '50%',
                    sm: '33.33%',
                    xl: isBundlePlan ? '25%' : '20%',
                  },
                  maxWidth: {
                    xs: '50%',
                    sm: '33.33%',
                    xl: isBundlePlan ? '25%' : '20%',
                  },
                  flexGrow: 0,
                }}
              >
                {renderRating({
                  setFieldValue,
                })}
              </Grid>
            )}
          </Grid>
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0.8,
              mt: 1.6,
            }}
          >
            <Button
              noRounder
              buttonType='primary-dark'
              sx={{
                minWidth: 'unset !important',
                padding: '4px 16px',
              }}
              onClick={() => formRef.current.handleSubmit()}
              disabled={isExpand && !dirty}
            >
              <Typography
                sx={{
                  minWidth: 'unset !important',
                  fontWeight: 500,
                }}
              >
                Apply
              </Typography>
            </Button>
            <Button
              buttonType='text'
              noRounder
              sx={{
                minWidth: 'unset !important',
                padding: '4px 16px',
                marginRight: '0px !important',
              }}
              onClick={() => {
                setIsExpand(false);
                resetForm();
                onClearAll();
              }}
            >
              <Typography
                sx={{
                  color: themes.color.violet500,
                  fontWeight: 500,
                }}
              >
                Cancel
              </Typography>
            </Button>
            <Divider orientation='vertical' flexItem />
            <Button
              buttonType='text'
              noRounder
              sx={{
                minWidth: 'unset !important',
                padding: '4px 16px',
                marginRight: '0px !important',
              }}
              onClick={() => {
                resetForm();
                onClearAll();
              }}
            >
              <Typography
                sx={{
                  color: themes.color.violet500,
                  fontWeight: 500,
                }}
              >
                Clear
              </Typography>
            </Button>
          </Stack>
        </StyledForm>
      );

    return (
      <StyledFilterStack>
        <Stack
          sx={{
            width: 300,
            [themes.breakpoints.down('md')]: {
              width: 'unset',
              flex: 1,
            },
          }}
        >
          <Field.SearchInput
            name='search'
            placeholder='Search'
            onClear={() => {
              onClear();
              setFieldValue('search', '');
            }}
            onChange={(e) => {
              setFieldValue('search', e.target.value);
              handleSearch();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                formRef.current.handleSubmit();
              }
            }}
          />
        </Stack>
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            rowGap: 2,
            columnGap: 0.8,
          }}
        >
          <Box
            sx={{
              width: 160,
              [themes.breakpoints.down('md')]: {
                display: 'none',
              },
            }}
          >
            {renderTaskStatus({
              setFieldValue,
            })}
          </Box>
          {!isBundlePlan && (
            <Box
              sx={{
                width: 160,
                [themes.breakpoints.down('md')]: {
                  display: 'none',
                },
              }}
            >
              {renderStore({
                setFieldValue,
              })}
            </Box>
          )}
          <Box
            sx={{
              width: 160,
              [themes.breakpoints.down('md')]: {
                display: 'none',
              },
            }}
          >
            {renderDriverList({
              setFieldValue,
            })}
          </Box>
          {tab === ITabValue.Completed && (
            <Box
              sx={{
                width: 160,
                [themes.breakpoints.down('md')]: {
                  display: 'none',
                },
              }}
            >
              {renderRating({
                setFieldValue,
              })}
            </Box>
          )}
          {!isEqual(values, defaultValues) && !isExpand && (
            <Button
              buttonType='text'
              onClick={() => {
                resetForm();
                onClearAll();
              }}
              sx={{
                '&.MuiButton-text': {
                  minWidth: 'unset !important',
                },
                [themes.breakpoints.down('md')]: {
                  display: 'none',
                },
              }}
            >
              <Typography
                sx={{
                  color: themes.color.violet500,
                  fontWeight: 500,
                }}
              >
                Clear
              </Typography>
            </Button>
          )}
          <Button
            buttonType='text'
            noRounder
            sx={{ minWidth: '35px !important' }}
            onClick={() => {
              navigate({
                pathname: location.pathname,
                search: toQueryString({
                  statuses,
                  executor_ids,
                  ...(tabValue === ITabValue.Completed && { rating }),
                  ...(!isBundlePlan && { created_by }),
                  tab,
                }),
              });
              resetForm();
              setIsExpand(true);
            }}
          >
            <Icon
              name='filter'
              baseUrl='map'
              color='#33363D'
              useBackgroundImg
              size={16}
            />
          </Button>
        </Stack>
      </StyledFilterStack>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      innerRef={formRef}
    >
      {({ values, setFieldValue, resetForm, dirty }) =>
        renderForm({ values, setFieldValue, resetForm, dirty })
      }
    </Formik>
  );
};
